import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Banner, Button, Field, Page, Spinner } from "../../components";
import { AuthLayout } from "../auth/AuthLayout";
import { ApiError } from "../../api/errors";
import { useAcceptInvitation } from "../../api/hooks";
import { t } from "../../i18n/t";
import { MIN_PASSWORD_LENGTH } from "../auth/passwordPolicy";

type Status = "pending" | "need_password" | "invalid" | "unexpected" | "success";

/**
 * `/auth/accept-invite?token=…` (P5-web §2h). ANONYMOUS — wired directly into `router.tsx`'s top-level route
 * list, not through `screens/practice/routes.tsx` (which sits under `RequireSession`). §"Auth semantics for
 * accept-invite" in the task file describes a login-required branch for an existing user with no session; the
 * COMMITTED contract's `accept_invitation` docstring says otherwise — "the token IS the authentication", no
 * session read or required — so that branch is not reachable against the real API and is not built here. What
 * IS built against the real contract: a first POST with no password, and — because `AcceptInvitationIn.password`
 * is optional and the contract carries no `new_user`/`password_required` status of its own — a `422
 * validation_failed` on that first attempt is read as "this email has no account yet, collect a password and
 * retry" rather than surfaced as a hard failure. `invalid_token` (unknown/expired/revoked/already-resolved, all
 * one code per §7's anti-enumeration rule) is shown as its own message and does not offer the password form.
 */
export function AcceptInvitationScreen(): JSX.Element {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const navigate = useNavigate();
  const acceptInvitation = useAcceptInvitation();
  const [status, setStatus] = useState<Status>("pending");
  const [password, setPassword] = useState("");
  const requested = useRef(false);

  useEffect(() => {
    if (token === "" || requested.current) return;
    requested.current = true;
    void attempt(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs once per mounted token, like VerifyEmailScreen.
  }, [token]);

  async function attempt(withPassword: string | null): Promise<void> {
    try {
      await acceptInvitation.mutateAsync({ token, body: { password: withPassword } });
      setStatus("success");
      navigate("/auth/login?accepted=1", { replace: true });
    } catch (caught) {
      if (caught instanceof ApiError && caught.is("invalid_token")) {
        setStatus("invalid");
      } else if (caught instanceof ApiError && caught.is("validation_failed") && withPassword === null) {
        setStatus("need_password");
      } else {
        setStatus("unexpected");
      }
    }
  }

  async function onSubmitPassword(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    await attempt(password);
  }

  if (token === "") {
    return (
      <AuthLayout>
        <Page narrow title={t("practice.accept_invite.title")}>
          <Banner tone="block">{t("practice.accept_invite.missing_token")}</Banner>
          <p>
            <Link to="/auth/login">{t("practice.accept_invite.go_to_login")}</Link>
          </p>
        </Page>
      </AuthLayout>
    );
  }

  if (status === "pending" || status === "success") {
    return (
      <AuthLayout>
        <Page narrow title={t("practice.accept_invite.title")}>
          <Spinner />
          <p>{t("practice.accept_invite.pending")}</p>
        </Page>
      </AuthLayout>
    );
  }

  if (status === "invalid") {
    return (
      <AuthLayout>
        <Page narrow title={t("practice.accept_invite.title")}>
          <Banner tone="block">{t("practice.accept_invite.invalid")}</Banner>
          <p>
            <Link to="/auth/login">{t("practice.accept_invite.go_to_login")}</Link>
          </p>
        </Page>
      </AuthLayout>
    );
  }

  if (status === "unexpected") {
    return (
      <AuthLayout>
        <Page narrow title={t("practice.accept_invite.title")}>
          <Banner tone="block">{t("practice.accept_invite.unexpected")}</Banner>
          <p>
            <Link to="/auth/login">{t("practice.accept_invite.go_to_login")}</Link>
          </p>
        </Page>
      </AuthLayout>
    );
  }

  // AuthFlows card 07 (D11: no practice name on the card — the token is unread before acceptance).
  return (
    <AuthLayout>
      <Page narrow title={t("practice.accept_invite.title")}>
        <form onSubmit={(event) => void onSubmitPassword(event)} className="card card--auth">
          <p className="authnote">{t("practice.accept_invite.need_password")}</p>
          <Field label={t("practice.accept_invite.password_label")}>
            {({ id, describedBy }) => (
              <input
                id={id}
                aria-describedby={describedBy}
                type="password"
                autoComplete="new-password"
                minLength={MIN_PASSWORD_LENGTH}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            )}
          </Field>
          <Button
            type="submit"
            variant="primary"
            className="button--auth"
            disabled={acceptInvitation.isPending || password.length < MIN_PASSWORD_LENGTH}
          >
            {t("practice.accept_invite.password_submit")}
          </Button>
        </form>
      </Page>
    </AuthLayout>
  );
}
